import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  Space,
  DatePicker,
  Radio,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { panStatusData } from "../../../data";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const WaterPanAdd = () => { 
  const urlWtrSrce = "water_sources/fetch_watersourcess";
  const urlWtrAbstr = "water_abstraction_mode/fetch_waterabstractionmodes";
  const urlCrop = "main_crop/fetch_maincrops";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlAgency = "api/v2/agency/get";
  const urlWtrUse = "water_use/fetch_wateruses";
  const urlCategory = "pan_category/fetch_pancategorys";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const {
    counties,
    subCounties,
    locations,
    subLocations,
    ward,
    constituency,
    subsidySources,
  } = useSelector((state) => state.fetch);

  const {
    activeObjPan,
    dataWtrSrc,
    dataWtrAbstr,
    dataCrop,
    dataPathway,
    dataAgency,
    dataWtrUse,
    dataCategory,

    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjPan: state.obj.activeObj["pan"],
    dataWtrSrc: state.fetch.data[urlWtrSrce] || [],
    dataWtrAbstr: state.fetch.data[urlWtrAbstr] || [],
    dataCrop: state.fetch.data[urlCrop] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataAgency: state.fetch.data[urlAgency] || [],
    dataWtrUse: state.fetch.data[urlWtrUse] || [],
    dataCategory: state.fetch.data[urlCategory] || [],

    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/water-pans");
  }

  const plainOptions = ["Yes", "No"];

  const [countyName, setCountyName] = useState(activeObjPan?.countyName ?? "");

  const onFinish = async (data) => {
    data.panId = activeObjPan?.panId ? activeObjPan?.panId : null;
    data.panCropsId = Array.isArray(data?.panCropsId)
      ? data?.panCropsId.join(",")
      : data?.panCropsId;

    data.panSourceFundsId = Array.isArray(data?.panSourceFundsId)
      ? data?.panSourceFundsId.join(",")
      : data?.panSourceFundsId;

    data.panPathwayId = Array.isArray(data?.panPathwayId)
      ? data?.panPathwayId.join(",")
      : data?.panPathwayId;

    data.panStartDate = normalizeDateToLocalYear(data?.panStartDate);
    data.panEndDate = normalizeDateToLocalYear(data?.panEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "water_pan/save_waterpan",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const initialFormValues = {
    ...activeObjPan,
    panCropsId: activeObjPan?.panCropsId
      ? activeObjPan?.panCropsId?.split(",")
      : [],

    panSourceFundsId: activeObjPan?.panSourceFundsId
      ? activeObjPan?.panSourceFundsId?.split(",")
      : [],
    panPathwayId: activeObjPan?.panPathwayId
      ? activeObjPan?.panPathwayId?.split(",")
      : [],

    panStartDate: activeObjPan?.panStartDate
      ? dayjs(new Date(activeObjPan?.panStartDate))
      : null,
    panEndDate: activeObjPan?.panEndDate
      ? dayjs(new Date(activeObjPan?.panEndDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjPan,
      panStartDate: activeObjPan?.panStartDate
        ? dayjs(new Date(activeObjPan?.panStartDate))
        : null,
      panEndDate: activeObjPan?.panEndDate
        ? dayjs(new Date(activeObjPan?.panEndDate))
        : null,
    });
    if (activeObjPan?.panSourceFundsId) {
      form.setFieldsValue({
        panSourceFundsId: activeObjPan?.panSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjPan?.panCropsId) {
      form.setFieldsValue({
        panCropsId: activeObjPan?.panCropsId?.split(",")?.map((b) => b?.trim()),
      });
    }

    if (activeObjPan?.panPathwayId) {
      form.setFieldsValue({
        panPathwayId: activeObjPan?.panPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjPan, form]);

  const [countyId, setCountyId] = useState(activeObjPan?.panCountyId ?? "");
  const [constituencyId, setConstituencyId] = useState(
    activeObjPan?.panConstId ?? ""
  );
  const [subCountyId, setSubCountyId] = useState(
    activeObjPan?.panSubCountyId ?? ""
  );
  const [locationId, setLocationId] = useState(
    activeObjPan?.panLocationId ?? ""
  );
  const [subLocationId, setSubLocationId] = useState(
    activeObjPan?.panSubLocationId ?? ""
  );
  const [wardId, setWardId] = useState(activeObjPan?.panWardId ?? "");

  const handleCountyChange = async (value) => {
    setCountyId(value);
    setConstituencyId("");
    setSubCountyId("");
    setLocationId("");
    setSubLocationId("");
    setWardId("");

    form.setFieldsValue({
      panCountyId: value,
      panConstId: "",
      panSubCountyId: "",
      panLocationId: "",
      panSubLocationId: "",
      panWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(
      fetchData({
        url: urlConstituency,
        constCountyId: value,
      })
    );
  };

  const handleConstituencyChange = async (value) => {
    setConstituencyId(value);
    setWardId("");

    form.setFieldsValue({
      panConstId: value,
      panWardId: "",
    });

    await dispatch(
      fetchData({
        url: urlWard,
        wdConstId: value,
      })
    );
  };

  const handleSubCountyChange = async (value) => {
    setSubCountyId(value);
    setLocationId("");
    setSubLocationId("");

    form.setFieldsValue({
      panSubCountyId: value,
      panLocationId: "",
      panSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleLocationChange = async (value) => {
    setLocationId(value);
    setSubLocationId("");

    form.setFieldsValue({
      panLocationId: value,
      panSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleSubLocationChange = (value) => {
    setSubLocationId(value);

    form.setFieldsValue({
      panSubLocationId: value,
    });
  };

  const handleWardChange = (value) => {
    setWardId(value);

    form.setFieldsValue({
      panWardId: value,
    });
  };

  const handleDatesChange = (_, allValues) => {
    const { panStartDate, panEndDate } = allValues;
    if (panStartDate && panEndDate) {
      const diff = dayjs(panEndDate)?.diff(dayjs(panStartDate), "day");
      form.setFieldsValue({
        panTimeFrame: `${diff} day${diff !== 1 ? "s" : ""}`,
      });
    } else {
      form.setFieldsValue({ panTimeFrame: "" });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlWtrAbstr,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlCrop,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrSrce,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrUse,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlCategory,
        limit: null,
        start: 0,
      })
    );

    dispatch(fetchSubsidySource());
  }, []);

  useEffect(() => {
    if (activeObjPan?.panCountyId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjPan?.panCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjPan?.panCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjPan?.panConstId,
        })
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjPan?.panSubCountyId,
        })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjPan?.panLocationId,
        })
      );
    }
  }, [activeObjPan, form]);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Water Pan", path: "/rob/water-pans" },
          { title: "Water Pan Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Water Pans Information</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={handleDatesChange}
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="panName"
                label={"Pan Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add name of pan",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>

              <Form.Item
                name="panCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {countyId && (
                <>
                  <Form.Item
                    name="panConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="panSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {subCountyId && (
                <Form.Item
                  name="panLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {locationId && (
                <Form.Item
                  name="panSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {constituencyId && (
                <Form.Item
                  name="panWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="panLat"
                label={"GPS Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="panLong"
                label={"GPS Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="panCost"
                label={"Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="panWaterSourceId"
                className=""
                label={"Water Source"}
                rules={[
                  {
                    required: true,
                    message: "Please select water source",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrSrc?.length > 0 &&
                    dataWtrSrc?.map((item) => ({
                      value: item?.wtrId,
                      label: item?.wtrSource,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="panVolume"
                label={"Capacity"}
                rules={[
                  {
                    required: true,
                    message: "Please add capacity",
                  },
                ]}
              >
                <InputNumber
                  suffix="M³"
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label={"Auxiliaries"}
                name="panAuxiliariesId"
                rules={[
                  {
                    required: true,
                    message: "Auxiliaries is required",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="panWaterAbstractionModeId"
                className=""
                label={"Mode of Water Abstraction"}
                rules={[
                  {
                    required: true,
                    message: "Please select Mode of water abstraction",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrAbstr?.length > 0 &&
                    dataWtrAbstr?.map((item) => ({
                      value: item?.waId,
                      label: item?.waType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="panEstBeneficiaries"
                label={"Estimated Benaficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add benaficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="panAcreageFarmed"
                label={"Acreage"}
                rules={[
                  {
                    required: true,
                    message: "Please add Acreage",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acres"
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Ksh. e.g 500"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label={"Crops type (Proposed/Existing)"}
                name="panCropsId"
                rules={[
                  {
                    required: true,
                    message: "Required field",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  style={{ height: "52px" }}
                  size="large"
                  className=""
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following crops
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                >
                  {dataCrop?.length > 0 &&
                    dataCrop?.map((item) => (
                      <Select.Option key={item?.cropId} value={item?.cropId}>
                        {item?.cropName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="panStatus"
                className=""
                label={"Pan Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select pan status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    panStatusData?.length > 0 &&
                    panStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                label="Start Date"
                name="panStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="End Date"
                name="panEndDate"
                rules={[
                  {
                    required: true,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Time Frame"
                name="panTimeFrame"
                rules={[
                  {
                    required: false,
                    message: "Time Frame  is required",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                label="Source of Funding"
                name="panSourceFundsId"
                rules={[
                  { required: false, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="panPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="panAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="panManagingAgency"
                label={"Managing Entity"}
                rules={[
                  {
                    required: true,
                    message: "Please add managing entity",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>

              <Form.Item
                name="panWaterUseId"
                className=""
                label={"Purpose/Water Use"}
                rules={[
                  {
                    required: true,
                    message: "Please select purpose/water use",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrUse?.length > 0 &&
                    dataWtrUse?.map((item) => ({
                      value: item?.wuId,
                      label: item?.wuType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="panLined"
                className=""
                label={"Is the Water Pan Lined? "}
                rules={[
                  {
                    required: true,
                    message: "Please add lined status",
                  },
                ]}
              >
                <Radio.Group options={plainOptions} />
              </Form.Item>

              <Form.Item
                label="Category"
                name="panCategoryId"
                rules={[
                  {
                    required: false,
                    message: "Category is required",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataCategory?.length > 0 &&
                    dataCategory?.map((item) => ({
                      value: item?.panId,
                      label: item?.panCategory,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
            </div>
            <Form.Item
              name="panRemarks"
              label={"Remarks"}
              rules={[
                {
                  required: true,
                  message: "Please add remarks",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjPan?.panId ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default WaterPanAdd;
